import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { readStoredValue, writeStoredValue } from '../lib/safeStorage';

export const VIEW_ROLE_STORAGE_KEY = 'quickDialViewRole';

type ViewRole = 'TenantAdmin' | 'Manager' | 'User';

interface RoleContextType {
  viewRole: ViewRole;
  setViewRole: (role: ViewRole) => void;
  isViewingAs: (role: ViewRole) => boolean;
  canAccess: (requiredRole: ViewRole) => boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

const roleHierarchy: Record<ViewRole, number> = {
  TenantAdmin: 3,
  Manager: 2,
  User: 1,
};

interface RoleProviderProps {
  children: ReactNode;
}

export function RoleProvider({ children }: RoleProviderProps) {
  const [viewRole, setViewRole] = useState<ViewRole>('TenantAdmin');

  useEffect(() => {
    // Load saved role from storage
    const savedRole = readStoredValue(VIEW_ROLE_STORAGE_KEY) as ViewRole | null;
    if (savedRole && roleHierarchy[savedRole]) {
      setViewRole(savedRole);
    }
  }, []);

  const handleSetViewRole = (role: ViewRole) => {
    setViewRole(role);
    writeStoredValue(VIEW_ROLE_STORAGE_KEY, role);
  };

  const isViewingAs = (role: ViewRole): boolean => {
    return viewRole === role;
  };

  const canAccess = (requiredRole: ViewRole): boolean => {
    return roleHierarchy[viewRole] >= roleHierarchy[requiredRole];
  };

  return (
    <RoleContext.Provider
      value={{
        viewRole,
        setViewRole: handleSetViewRole,
        isViewingAs,
        canAccess,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRoleContext(): RoleContextType {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
}
